import { useQuery } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import Loading from 'src/pages/Status/Loading';
import {
  fetchCocRecommendationSummary,
  fetchRecommendationAssigneesRole,
  fetchSubmissionContextRoleAssignments
} from '../api/queries';
import { useParams } from 'react-router-dom';
import { useSubmissionData } from './SubmissionDataProvider';
import {
  IGetCOCRecommedationSummaryResponse,
  IGetSubmissionContextRoleAssignmentsResponse,
  IGetSubmissionRecommendationAssigneesResponse
} from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { useKeycloak } from 'src/components/AuthHOC';
import { useSubmissionContextData } from './SubmissionContextProvider';

interface ICOCContextData {
  rolesAssignments: IGetSubmissionContextRoleAssignmentsResponse;
  recommendationAssignees: IGetSubmissionRecommendationAssigneesResponse;
  currentUserRoles: string[];
  currentUserRole: string | undefined;
  currentUserID: string | undefined;
  recommedationSummary: IGetCOCRecommedationSummaryResponse | undefined;
  isCurrentOwner: boolean;
  isRecommendationMaker: boolean;
  isDecisionMaker: boolean;
  isCurrentRecommendationState: boolean;
  isCurrentDecisionState: boolean;
}

type COCConfigWithDecisionMaker = {
  isDecisionMaker?: boolean;
};

export const COCDataContext = createContext<ICOCContextData | undefined>(
  undefined
);

export const useCOCContextData = (): ICOCContextData => {
  const context = useContext(COCDataContext);
  if (!context) {
    throw new Error('useCOCContextData must be used within a COCDataProvider');
  }
  return context;
};

const COCDataProvider = ({ children }: { children: JSX.Element }) => {
  const { ctxId = '' } = useParams();
  const { workflowResponse } = useSubmissionData();
  const { submissionContext } = useSubmissionContextData();
  const { submissionId } = workflowResponse;
  const { currentUser } = useKeycloak();

  const { data } = useQuery({
    queryKey: ['roles-assignments', ctxId],
    queryFn: () => fetchSubmissionContextRoleAssignments(ctxId)
  });
  const { data: recommendationAssigneesData } = useQuery({
    queryKey: ['recommendation-assignees', submissionId],
    queryFn: () => fetchRecommendationAssigneesRole(submissionId)
  });

  const { data: recommedationSummary } = useQuery({
    queryKey: ['recommendation-summary', submissionId],
    queryFn: () => fetchCocRecommendationSummary(submissionId)
  });

  const currentUserAssignments =
    recommendationAssigneesData?.recommendationAssignees?.filter(
      user => user.userId === currentUser.id
    ) ?? [];

  const workflowCurrentRole =
    recommedationSummary?.recommendationWorkflowData?.currentRole;

  if (!data || !recommendationAssigneesData) return <Loading />;

  const currentUserRoles = Array.from(
    new Set(currentUserAssignments.map(assignment => assignment.role))
  );
  const currentUserData =
    currentUserAssignments.find(user => user.role === workflowCurrentRole) ??
    currentUserAssignments[0];
  const currentUserRole =
    (workflowCurrentRole && currentUserRoles.includes(workflowCurrentRole)
      ? workflowCurrentRole
      : undefined) ??
    currentUserData?.role;
  const currentUserID = currentUserData?.userId ?? currentUser.id;
  const matchingCocConfigs =
    submissionContext?.config.cocConfig?.filter(user =>
      currentUserRoles.includes(user.role)
    ) ?? [];
  const cocConfig =
    matchingCocConfigs.find(user => user.role === currentUserRole) ??
    matchingCocConfigs[0];
  const isCurrentOwner = cocConfig?.isOwner ?? false;
  const isRecommendationMaker = !isCurrentOwner && !cocConfig?.autoAssign;
  const isDecisionMaker = matchingCocConfigs.some(
    user => !!(user as COCConfigWithDecisionMaker | undefined)?.isDecisionMaker
  );
  const isCurrentRecommendationState =
    !!recommedationSummary?.recommendationWorkflowData?.currentRole &&
    currentUserRoles.includes(
      recommedationSummary?.recommendationWorkflowData?.currentRole
    );
  const isCurrentDecisionState =
    !!recommedationSummary?.recommendationWorkflowData?.currentRole &&
    currentUserRoles.includes(
      recommedationSummary?.recommendationWorkflowData?.currentRole
    );

  return (
    <COCDataContext.Provider
      value={{
        rolesAssignments: data,
        recommendationAssignees: recommendationAssigneesData,
        currentUserRoles,
        currentUserRole: currentUserRole,
        currentUserID: currentUserID,
        recommedationSummary,
        isCurrentOwner,
        isRecommendationMaker,
        isDecisionMaker,
        isCurrentRecommendationState,
        isCurrentDecisionState
      }}
    >
      {children}
    </COCDataContext.Provider>
  );
};

export default COCDataProvider;
